import EditIcon from '@mui/icons-material/Edit'
import { Box, IconButton, Stack } from '@mui/material'
import type { ReactNode } from 'react'

import CustomTypography from '@/components/dataDisplay/CustomTypography'

/**
 * Side of the square indicator slot. Sized to the small IconButton's glyph so the indicator, the
 * label's x-height and the pencil sit on one optical line.
 */
const INDICATOR_SIZE = 20

/**
 * The `+` / `−` a metering port's arithmetic relation is drawn with, in the table and the graph alike.
 *
 * A boxed typographic glyph rather than a coloured icon: the operation is a category, not a state,
 * so red for subtractive would say "wrong" where nothing is wrong. Using the same glyph the graph
 * node's subtitle uses keeps the two views reading the same. Hidden from assistive technology
 * because the label beside it already says the word.
 */
export function OperatorGlyph({ operation }: Readonly<{ operation: 'PLUS' | 'MINUS' }>) {
  return (
    <Box
      aria-hidden
      component="span"
      sx={{
        display: 'grid',
        placeItems: 'center',
        width: INDICATOR_SIZE,
        height: INDICATOR_SIZE,
        borderRadius: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
      }}
    >
      {/*
       * Drawn, not typeset. A typographic minus sits on the font's math axis, so any nudge that
       * centres it against one font lands off-centre in another. Two strokes through the middle of
       * their own viewBox are centred by construction: no font involved, nothing to tune, and the
       * same on every platform. `currentColor` keeps the box's `text.secondary`.
       */}
      <Box component="svg" display="block" height={10} viewBox="0 0 10 10" width={10}>
        <line stroke="currentColor" strokeLinecap="round" strokeWidth={1.5} x1={1.75} x2={8.25} y1={5} y2={5} />
        {operation === 'PLUS' && (
          <line stroke="currentColor" strokeLinecap="round" strokeWidth={1.5} x1={5} x2={5} y1={1.75} y2={8.25} />
        )}
      </Box>
    </Box>
  )
}

export type PortSettingValueProps = {
  /** What the setting is called ("Relation", "Measure"). Drawn as a caption before the value. */
  name: string
  /** Small mark that keys the value: an operator glyph or an icon sized to the slot. */
  indicator: ReactNode
  /** The value as words. This, not the indicator, carries the meaning. */
  label: string
  /** Accessible name of the edit control, naming what gets edited (WCAG 2.4.4). */
  editLabel: string
  onEdit: () => void
}

/**
 * The read state of an editable value in the ports table's Settings column.
 *
 * Two rows, always: the caption above, then indicator, label and pencil. Stacked is deliberate, not
 * a fallback. Inline aligns better — a two-line block centred among one-line cells puts its value
 * about 11px below the row midline — but it needs a 231px column against stacked's 169px, and on a
 * table this crowded the 62px was worth more than the alignment.
 *
 * What must not come back is stacking produced by *wrapping*. A `flexWrap` fallback let ordinary
 * values degrade to two lines, so the column rendered mixed, some cells inline and some stacked,
 * which is worse than either shape chosen outright.
 *
 * The pencil sits right after the label rather than at the column edge, so what is edited and how
 * are one unit. Both the measure and the relation render through this, so the column has one
 * vocabulary.
 */
export default function PortSettingValue({
  name,
  indicator,
  label,
  editLabel,
  onEdit,
}: Readonly<PortSettingValueProps>) {
  return (
    <Stack alignItems="flex-start" rowGap={0.25}>
      {/*
       * No floor width on the caption any more: that existed to make both settings' values start at
       * the same x while they shared a line with it. On its own row the caption sizes to its text,
       * and the values below already start at the same x because each row begins at the cell edge.
       */}
      <CustomTypography color="grey.500" component="span" variant="caption">
        {name}
      </CustomTypography>
      <Stack alignItems="center" direction="row" gap={0.75}>
        <Box
          component="span"
          sx={{
            display: 'grid',
            placeItems: 'center',
            flex: 'none',
            width: INDICATOR_SIZE,
            height: INDICATOR_SIZE,
            color: 'text.secondary',
            fontSize: 18,
          }}
        >
          {indicator}
        </Box>
        <CustomTypography component="span" variant="inherit">
          {label}
        </CustomTypography>
        <IconButton
          aria-label={editLabel}
          size="small"
          // The negative block margin keeps the 26px hit area from sizing the line: beside 20px text
          // it pushed the value's centre 3px below the caption's and the neighbouring cells'.
          sx={{ ml: -0.25, my: -0.375, color: 'text.secondary', fontSize: 16 }}
          onClick={onEdit}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Stack>
    </Stack>
  )
}
